/**
 * Per-session dismissal of the grace-period MFA enrollment banner.
 *
 * `sessionStorage`, deliberately not `localStorage` and deliberately not the server: the story
 * requires the banner to come back on the user's *next* session until they enrol, and the backend
 * stores nothing and offers no dismiss endpoint. `localStorageService` is therefore not reused here —
 * it writes to the wrong store for this purpose.
 *
 * Keyed by user id because one browser session can describe several users: impersonating someone and
 * stopping again both swap who `/Profile` is about, and an unkeyed flag would let one user's
 * dismissal silence another user's banner.
 */

const KEY_PREFIX = 'mfaEnrollmentBannerDismissed:';
const DISMISSED = 'true';

const keyFor = (userId: number): string => `${KEY_PREFIX}${userId}`;

export const isMfaBannerDismissed = (userId: number): boolean => {
  try {
    return window.sessionStorage.getItem(keyFor(userId)) === DISMISSED;
  } catch {
    // Private-mode Safari and hardened browser settings can throw on access. Answering "not
    // dismissed" only costs the user one extra look at a banner they can dismiss again.
    return false;
  }
};

export const dismissMfaBanner = (userId: number): void => {
  try {
    window.sessionStorage.setItem(keyFor(userId), DISMISSED);
  } catch {
    // Same story: the dismissal then lives only in component state and does not survive a reload.
  }
};
